import { useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import { getUserTypeFromToken } from '@luna-protocol/core/src/utils/getCustomerIdFromToken';
import { useGetEmailRelatedDealers } from '../../queries/useGetEmailRelatedDealers';
import { usePostLoginSwitch } from '../../queries/usePostLoginSwitch';
import './DealerSwitcher.scss';

const DealerSwitcher = () => {
  const [token, setToken] = useState(() => localStorage.getItem('user_token'));
  const [currentEntityId, setCurrentEntityId] = useState(() => localStorage.getItem('dealer_id'));

  useInterval(() => {
    setToken(localStorage.getItem('user_token'));
    setCurrentEntityId(localStorage.getItem('dealer_id'));
  }, 500);

  const userType = token ? getUserTypeFromToken(token) : null;
  const isDealer = userType === 'dealer';

  const { data: dealers } = useGetEmailRelatedDealers(isDealer);
  const { switchDealer, isLoading, isError } = usePostLoginSwitch();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const uniqueDealers = dealers
    ? dealers.reduce<typeof dealers>((acc, dealer) => {
        if (!acc.some(d => d.entity_id === dealer.entity_id)) {
          acc.push(dealer);
        }
        return acc;
      }, [])
    : [];

  if (!isDealer || uniqueDealers.length <= 1) {
    return null;
  }

  const currentUser = uniqueDealers.find(d => d.entity_id === currentEntityId);
  const currentDealerName = currentUser?.dealer_name ?? 'Dealer';

  const handleSelect = (userId: string) => {
    setOpen(false);
    switchDealer(
      { user_id: userId },
      {
        onSuccess: () => {
          window.location.href = '/prequalification';
        },
      },
    );
  };

  return (
    <div className="dealer-switcher" ref={containerRef}>
      <button
        className="dealer-switcher__trigger link border-left"
        onClick={() => setOpen(prev => !prev)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={open}>
        {isLoading ? 'Switching...' : currentDealerName}
        <span className="dealer-switcher__caret" aria-hidden="true" />
      </button>
      {open && (
        <>
          <div className="dealer-switcher__overlay" onClick={() => setOpen(false)} />
          <ul className="dealer-switcher__menu" role="listbox" aria-label="Switch dealer">
            {uniqueDealers.map(dealer => (
              <li
                key={dealer.user_id}
                role="option"
                aria-selected={dealer.entity_id === currentEntityId}
                className={`dealer-switcher__option${dealer.entity_id === currentEntityId ? ' dealer-switcher__option--current' : ''}`}
                onClick={() => dealer.entity_id !== currentEntityId && handleSelect(dealer.user_id)}>
                {dealer.dealer_name}
                {dealer.entity_id === currentEntityId && <span className="dealer-switcher__option-badge">✓</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {isError && <span className="dealer-switcher__error">Switch failed. Please try again.</span>}
    </div>
  );
};

export default DealerSwitcher;
